import * as React from "react";
import { Badge, Button, Icon } from "@mui/material";
import { useBreakpoint } from "../../hooks/useBreakpoint";
import { useXoraiaValue } from "../../hooks/useXoraia";
import { TUseXElement, useXElement } from "../../hooks/useXElement";
import { USE_SELECTED_INDEX } from "../../hooks/useSelectedElement";
import { useSetRecoilState } from "recoil";
import { getValueOnBreakpoint } from "../../lib/getValueOnBreakpoint";
import { clickHandler } from "../../lib/clickHandler";
import { XElementConfig } from "../../utils/interfaces/Elements/XElement.interfaces";

import { isEqual } from "lodash";
import { generateSide } from "../../lib/generateSide";
import { generateBorder } from "../../lib/generateBorder";
import { generateRadius } from "../../lib/generateRadius";
import {
  IXButtonProps,
  XButtonProps,
} from "../../utils/interfaces/Elements/XButton.interfaces";

const compareProps = (prevProps: any, nextProps: any) => {
  return isEqual(prevProps, nextProps);
};

const XButton: React.FC<XButtonProps> = React.memo(
  ({ config = {} as XElementConfig }) => {
    // WE TRACK THE BREAK POINTS CHANGES
    const breakpoint = useBreakpoint();

    const { env, Link } = useXoraiaValue();

    // WE ARE GETTING THE CURRENT ELEMENT FROM THE ATOM FAMILY
    const { xElement } = useXElement<TUseXElement<IXButtonProps>>(config.index);

    // GARBING THE INDEX UPDATER FOR GENERAL USAGE
    const setSelectedElement = useSetRecoilState(USE_SELECTED_INDEX);

    // IF WE DONT HAVE THIS ELEMENT CONFIGS ADDED WE DON'T SHOW IT
    if (!xElement || Object.keys(xElement).length === 0) return null;

    // WE GET ALL DEVICES PROPS AS VALUE
    const { value } = xElement;

    // WE NOW USE THE CURRENT BREAKPOINT VALUE ON THIS ELEMENT
    const valueAtBreakPoint = getValueOnBreakpoint<IXButtonProps>(
      value as any,
      breakpoint
    );

    const devProps = {
      id: config.index,
      onClick: (e: any) => clickHandler(e, setSelectedElement, config.index),
    };

    const {
      text,
      href,
      hoverColor,
      color,
      bgColor,
      hoverFontSize,
      hoverTextDecoration,
      hoverBgColor,
      hoverBorder,
      hoverRadius,
      border,
      borderRadius,
      boxShadow = "none",

      padding,
      margin,
      icon,
      iconPosition,
      iconClassName,
      iconColor,
      iconFontSize,
      // iconPack,
      badgeColor,
      badgeMode,
      badgeTextColor,
      ...sx
    } = valueAtBreakPoint;

    if (!text && !icon && env === "prod") return null;

    const _icon = (
      <Icon
        baseClassName={iconClassName}
        sx={{
          color: iconColor,
          fontSize: iconFontSize,
        }}
      >
        {icon}
      </Icon>
    );

    const hasBadge = badgeMode && badgeMode !== "none";

    let _btn = (
      <Button
        LinkComponent={Link}
        disableRipple={!!href}
        href={env === "prod" ? href : undefined}
        variant="contained"
        color="inherit"
        startIcon={text && icon && iconPosition !== "right" ? _icon : null}
        endIcon={text && icon && iconPosition === "right" ? _icon : null}
        {...(env === "dev" && devProps)}
        onClick={(e) => {
          if (env === "dev") devProps.onClick(e);
          // TODO DO EXTRA CLICK ACTION HERE
        }}
        sx={{
          ...sx,
          boxShadow,
          borderRadius: generateRadius(borderRadius),
          border: generateBorder(border),
          bgcolor: bgColor,
          cursor: "pointer",
          minWidth: "auto",
          color: color,
          padding: generateSide("padding", padding),
          margin: hasBadge ? 0 : generateSide("margin", margin),
          textTransform: "none",
          "&:hover": {
            color: hoverColor ?? color,
            fontSize: hoverFontSize ?? sx.fontSize,
            textDecoration: hoverTextDecoration ?? sx.textDecoration,
            bgcolor: hoverBgColor ?? bgColor,
            border: generateBorder(hoverBorder ?? border),
            borderRadius: generateRadius(hoverRadius ?? borderRadius),
            boxShadow,
          },
        }}
      >
        {text || _icon || "MY BUTTON"}
        {hasBadge && badgeMode === "text" && "(0)"}
      </Button>
    );

    if (hasBadge && badgeMode !== "text") {
      _btn = (
        <Badge
          badgeContent={2}
          variant={badgeMode === "bubble" ? "standard" : "dot"}
          color="secondary"
          sx={{
            bgcolor: badgeColor,
            color: badgeTextColor,
            maxWidth: "max-content",
            maxHeight: "max-content",
            margin: generateSide("margin", margin),
          }}
        >
          {_btn}
        </Badge>
      );
    }

    return _btn;
  },
  compareProps
);

export default XButton;
